import React, { useEffect, useState } from "react";
import { fmtInt, pct, type BandejaFocus, type ToneCount } from "../sandbox/resumenData";
import { MobileScreen } from "./MobileChrome";

type ToneKey = "pos" | "neg" | "neu" | "none";
type ToneFilter = "all" | "neg" | "pos" | "neu";
type Trend = { text: string; tone: "good" | "bad" | "flat" };

export interface MobileTopic {
  key: string;
  name: string;
  total: number;
  tone: ToneCount;
  negNow: number;
  trend: Trend;
  low: boolean;
}

export interface MobileMention {
  id: string;
  channel: string;
  place: string;
  stars: number;
  date: string;
  post: string | null;
  text: string;
  tone: ToneKey;
  answered: boolean;
  gbp: boolean;
  paid: boolean;
  feedbackId: string;
  source: "google" | "meta";
}

interface Props {
  theme: "light" | "dark";
  pills: { id: string; label: string }[];
  source: string;
  onSource: (id: string) => void;
  topics: MobileTopic[];
  initialTopic: string | null;
  mentionsOf: (key: string, filter: ToneFilter) => MobileMention[];
  placesOf: (key: string) => { focus: "neg" | "pos"; rows: [string, number][] };
  bandejaGroup: "google" | "meta";
  onOpenBandeja: (focus?: BandejaFocus) => void;
}

const TONE_LABEL: Record<ToneKey, string> = { pos: "Positiva", neg: "Negativa", neu: "Neutra", none: "Sin clasificar" };
const TONE_CLASS: Record<ToneKey, string> = { pos: "good", neg: "bad", neu: "stable", none: "stable" };
const PAGE = 15;

function Tone({ tone }: { tone: ToneCount }) {
  const classified = tone.pos + tone.neu + tone.neg;
  const w = (n: number) => `${classified > 0 ? ((n / classified) * 100).toFixed(1) : 0}%`;
  return (
    <div className="sbx-tonebar rz-tone" role="img" aria-label={`${tone.pos} positivas, ${tone.neu} neutras, ${tone.neg} negativas`}>
      <i className="pos" style={{ width: w(tone.pos) }} />
      <i className="neu" style={{ width: w(tone.neu) }} />
      <i className="neg" style={{ width: w(tone.neg) }} />
    </div>
  );
}

/** Temas en el teléfono: lista ordenada por quejas; cada tema abre su pantalla con las menciones. */
export function TemasMobile(p: Props) {
  const [open, setOpen] = useState<string | null>(p.initialTopic);
  const [filter, setFilter] = useState<ToneFilter>("all");
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    setOpen(p.initialTopic);
  }, [p.initialTopic]);

  const topic = p.topics.find((t) => t.key === open) ?? null;
  const close = () => {
    setOpen(null);
    setFilter("all");
    setShown(PAGE);
  };

  const mentions = topic ? p.mentionsOf(topic.key, filter) : [];
  const places = topic ? p.placesOf(topic.key) : null;
  const maxPlace = places && places.rows.length > 0 ? places.rows[0][1] : 1;

  return (
    <div className="rz">
      {p.pills.length > 0 && (
        <div className="rz-chips" role="group" aria-label="Filtrar por canal">
          {p.pills.map((c) => (
            <button key={c.id} type="button" aria-pressed={p.source === c.id} onClick={() => p.onSource(c.id)}>
              {c.label}
            </button>
          ))}
        </div>
      )}

      {p.topics.length === 0 ? (
        <p className="rz-empty">No hay comentarios con tema asignado en este período.</p>
      ) : (
        <ul className="rz-list" aria-label="Temas del período">
          {p.topics.map((t) => (
            <li key={t.key}>
              <button type="button" className="rz-row tap" onClick={() => setOpen(t.key)}>
                <div className="main">
                  <strong>{t.name}</strong>
                  <Tone tone={t.tone} />
                  <span className="meta">
                    {fmtInt(t.total)} {t.total === 1 ? "mención" : "menciones"}
                    {t.low ? " · pocas" : ""}
                  </span>
                </div>
                <div className="side">
                  {t.negNow > 0 ? (
                    <span className="sbx-loc-tag bad">
                      {t.negNow} {t.negNow === 1 ? "queja" : "quejas"}
                    </span>
                  ) : (
                    <span className="sbx-loc-tag good">Sin quejas</span>
                  )}
                </div>
                <span className="chev" aria-hidden="true">
                  ›
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {topic && (
        <MobileScreen title={topic.name} theme={p.theme} onBack={close}>
          <div className="rz">
            <dl className="rz-stats">
              <div>
                <dt>Menciones</dt>
                <dd>{fmtInt(topic.total)}</dd>
              </div>
              <div>
                <dt>Quejas</dt>
                <dd className={topic.negNow > 0 ? "bad" : undefined}>{fmtInt(topic.negNow)}</dd>
              </div>
              <div>
                <dt>Negativas</dt>
                <dd>{pct(topic.tone.neg, topic.total)}%</dd>
              </div>
            </dl>
            <span className={`rz-trend ${topic.trend.tone}`}>{topic.trend.text}</span>

            {places && places.rows.length > 0 && (
              <section className="rz-places" aria-label={places.focus === "neg" ? "Dónde se concentran las quejas" : "Dónde se destaca"}>
                <h3>{places.focus === "neg" ? "Dónde se concentran" : "Dónde se destaca"}</h3>
                {places.rows.slice(0, 5).map(([name, n]) => (
                  <div key={name} className="rz-place">
                    <span className="n">{name}</span>
                    <div className="rh-track">
                      <i style={{ width: `${(n / maxPlace) * 100}%`, background: places.focus === "neg" ? "var(--sbx-crit)" : "var(--sbx-accent)" }} />
                    </div>
                    <b>{n}</b>
                  </div>
                ))}
              </section>
            )}

            <div className="rz-chips" role="group" aria-label="Filtrar por tono">
              {(
                [
                  ["all", "Todas", topic.total],
                  ["neg", "Negativas", topic.tone.neg],
                  ["pos", "Positivas", topic.tone.pos],
                  ["neu", "Neutras", topic.tone.neu],
                ] as [ToneFilter, string, number][]
              ).map(([id, label, n]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={filter === id}
                  onClick={() => {
                    setFilter(id);
                    setShown(PAGE);
                  }}
                >
                  {label} {n}
                </button>
              ))}
            </div>

            {mentions.length === 0 ? (
              <p className="rz-empty">No hay menciones con este tono.</p>
            ) : (
              <ul className="rz-mentions">
                {mentions.slice(0, shown).map((m) => (
                  <li key={m.id} className={`rz-men ${m.tone}`} data-origen={m.paid ? "paid" : "organic"}>
                    <div className="top">
                      <span>
                        <b>{m.channel}</b> · {m.place}
                        {m.stars > 0 ? ` · ★ ${m.stars}` : ""}
                      </span>
                      <span>{m.date}</span>
                    </div>
                    {m.post && <span className="ctx">En: «{m.post}»</span>}
                    <p>{m.text}</p>
                    <div className="tags">
                      <span className={`sbx-loc-tag ${TONE_CLASS[m.tone]}`}>{TONE_LABEL[m.tone]}</span>
                      <span className="sbx-loc-tag stable">{m.answered ? "Respondida" : "Sin responder"}</span>
                    </div>
                    {!m.answered && (
                      <button
                        type="button"
                        onClick={() => {
                          close();
                          p.onOpenBandeja({ topic: topic.key, feedbackId: m.feedbackId, group: m.source });
                        }}
                      >
                        {m.gbp ? "Responder en la Bandeja" : "Ver en la Bandeja"}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {mentions.length > shown && (
              <button type="button" className="rz-more" onClick={() => setShown((n) => n + PAGE)}>
                Ver más ({mentions.length - shown})
              </button>
            )}
            <button
              type="button"
              className="rz-more"
              onClick={() => {
                close();
                p.onOpenBandeja({ topic: topic.key, group: p.bandejaGroup });
              }}
            >
              Ver el tema en la Bandeja
            </button>
          </div>
        </MobileScreen>
      )}
    </div>
  );
}
